import { useCallback, useMemo } from "react";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, type InfiniteData } from "@tanstack/react-query";
import { toast } from "sonner";
import { queryClient } from "@/lib/queryClient";
import { CHECKLIST_ITEM_STATUS_TYPE } from "@/other/constants/OtherKeys.jsx";
import { createChecklistItem as buildChecklistItem, isDraftId, nextDraftId, type Checklist, type ChecklistItem } from "../types";
import {
  createChecklist,
  createChecklistItem,
  deleteChecklist,
  deleteChecklistItem,
  duplicateChecklist,
  exportChecklistItems,
  fetchChecklistDetail,
  fetchChecklistItemStatusLog,
  fetchEntityChecklists,
  importChecklistItems,
  patchChecklist,
  patchChecklistItem,
  removeChecklistItemImage as removeChecklistItemImageRequest,
  sendChecklistNudge,
  uploadChecklistItemImage as uploadChecklistItemImageRequest
} from "./checklist.service";
import type {
  ChecklistItemImageUploadResponse,
  ChecklistItemStatusLogRow,
  CreateChecklistItemPayload,
  CreateChecklistPayload,
  ChecklistListProgress,
  DuplicateChecklistPayload,
  EntityChecklistsPage,
  FetchChecklistItemStatusLogParams,
  PatchChecklistItemPayload,
  PatchChecklistPayload
} from "./checklist.types";
import {
  mergeChecklistRecordWrite,
  type ChecklistDetail,
  type ChecklistDetailMapOptions
} from "../utils/checklistApiAdapters";
import { checklistKeys, type ChecklistEntityPathType } from "./checklist.query-keys";

export const ENTITY_CHECKLISTS_PAGE_SIZE = 10;
/** Board-wide Checklists view rail (not progress overview). */
export const BOARD_CHECKLISTS_PAGE_SIZE = 15;

type ChecklistItemCacheScope = {
  boardId: number;
  checklistId?: number | string | null;
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null;
};

export interface UseChecklistsParams {
  boardId: number | null;
  /** Omit for board-wide Checklists view list. */
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null | undefined;
  /** Board-wide list only. */
  archive?: boolean;
  /** Board-wide list only. */
  progress?: ChecklistListProgress[];
  /** Board-wide list — HQ `assigned_to`. */
  assignedTo?: number | null;
  /** Board-wide list — HQ `entity_type` (not entity Manager scope). */
  filterEntityType?: ChecklistEntityPathType | null;
  /** Board-wide list — HQ `created_within_days`. */
  createdWithinDays?: 7 | 30 | 90 | null;
  /** Board-wide list — HQ `search` (checklist title). */
  search?: string | null;
  /** Board-wide list — HQ `recurrence` (comma-separated). */
  recurrence?: string | null;
  enabled?: boolean;
}

export interface UseChecklistsResult {
  checklists: Checklist[];
  totalCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<Checklist[]>;
}

type ChecklistsQueryKey =
  | ReturnType<typeof checklistKeys.entity>
  | ReturnType<typeof checklistKeys.boardList>
  | typeof checklistKeys.all;

/** React Query infinite list — entity Manager or board-wide (no entity filter). */
export function useChecklistsQuery({
  boardId,
  entityType = null,
  entityId = null,
  archive = false,
  progress,
  assignedTo = null,
  filterEntityType = null,
  createdWithinDays = null,
  search = null,
  recurrence = null,
  enabled = true
}: UseChecklistsParams): UseChecklistsResult {
  const isBoardWide = entityType == null || entityId == null || entityId === "";
  // Progress-filtered "Right now" overview is fetched whole (no limit/offset).
  const isUnpaged = isBoardWide && Boolean(progress?.length);
  const pageSize = isBoardWide ? BOARD_CHECKLISTS_PAGE_SIZE : ENTITY_CHECKLISTS_PAGE_SIZE;
  const canQuery = enabled && boardId != null && (isBoardWide || (entityType != null && entityId != null));

  const queryKey: ChecklistsQueryKey = !canQuery
    ? checklistKeys.all
    : isBoardWide
      ? checklistKeys.boardList(boardId, archive, {
          progress: progress?.length ? progress.join(",") : null,
          assignedTo,
          filterEntityType,
          createdWithinDays,
          search,
          recurrence
        })
      : checklistKeys.entity(boardId, entityType!, entityId!);

  const result = useInfiniteQuery<
    EntityChecklistsPage,
    Error,
    InfiniteData<EntityChecklistsPage>,
    ChecklistsQueryKey,
    number
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      try {
        return await fetchEntityChecklists({
          boardId: boardId!,
          entityType: isBoardWide ? null : entityType,
          entityId: isBoardWide ? null : entityId,
          limit: isUnpaged ? undefined : pageSize,
          offset: isUnpaged ? (pageParam > 0 ? pageParam : undefined) : pageParam,
          archive: isBoardWide ? archive : undefined,
          progress: isBoardWide ? progress : undefined,
          assignedTo: isBoardWide ? assignedTo : undefined,
          filterEntityType: isBoardWide ? filterEntityType : undefined,
          createdWithinDays: isBoardWide ? createdWithinDays : undefined,
          search: isBoardWide ? search : undefined,
          recurrence: isBoardWide ? recurrence : undefined
        });
      } catch {
        toast.error(isBoardWide ? "Unable to load Checklists." : "Unable to load checklists.");
        throw new Error(isBoardWide ? "Unable to load Checklists." : "Unable to load checklists.");
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.results.length === 0) return undefined;
      if (isUnpaged) {
        const loaded = allPages.reduce((total, page) => total + page.results.length, 0);
        const total = Number(lastPage.count);
        return Number.isFinite(total) && loaded < total ? loaded : undefined;
      }
      const loaded = lastPageParam + lastPage.results.length;
      const total = Number(lastPage.count);
      if (Number.isFinite(total) && total >= 0) {
        return loaded < total ? loaded : undefined;
      }
      return lastPage.results.length >= pageSize ? loaded : undefined;
    },
    enabled: canQuery,
    staleTime: 30_000,
    // Paged rail keeps its rows while a new filter/search loads, instead of flashing empty.
    placeholderData: isBoardWide && !isUnpaged ? keepPreviousData : undefined
  });

  const checklists = useMemo(
    () => result.data?.pages.flatMap((page) => page.results) ?? [],
    [result.data]
  );

  const totalCount = result.data?.pages[0]?.count ?? 0;

  const fetchNextPage = useCallback(() => {
    if (!result.hasNextPage || result.isFetchingNextPage) return;
    void result.fetchNextPage();
  }, [result.hasNextPage, result.isFetchingNextPage, result.fetchNextPage]);

  const refetch = useCallback(async (): Promise<Checklist[]> => {
    const next = await result.refetch();
    if (next.error) return [];
    return next.data?.pages.flatMap((page) => page.results) ?? [];
  }, [result.refetch]);

  return {
    checklists,
    totalCount,
    fetchNextPage,
    hasNextPage: result.hasNextPage ?? false,
    isFetchingNextPage: result.isFetchingNextPage,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error ?? null,
    refetch
  };
}

export interface UseChecklistDetailParams extends ChecklistDetailMapOptions {
  boardId: number | null;
  checklistId: number | string | null | undefined;
  enabled?: boolean;
}

/** React Query wrapper for HQ checklist retrieve (tile detail / editor). */
export function useChecklistDetailQuery({
  boardId,
  checklistId,
  assignedBoardUserIds,
  canEditGrant,
  canViewGrant,
  enabled = true
}: UseChecklistDetailParams) {
  const canQuery =
    enabled && boardId != null && checklistId != null && checklistId !== "" && !isDraftId(checklistId);

  return useQuery({
    queryKey: canQuery ? checklistKeys.detail(boardId, checklistId) : [...checklistKeys.all, "detail", "idle"],
    queryFn: async () => {
      try {
        return await fetchChecklistDetail({
          boardId: boardId!,
          checklistId: checklistId!,
          assignedBoardUserIds,
          canEditGrant,
          canViewGrant
        });
      } catch {
        toast.error("Unable to load this checklist.");
        throw new Error("Unable to load this checklist.");
      }
    },
    enabled: canQuery,
    staleTime: 30_000,
    retry: false,
    // Editor/tile reopen should always show current period state, not a 30s-old cache entry.
    refetchOnMount: "always"
  });
}

export function invalidateEntityChecklists(
  boardId: number,
  entityType?: ChecklistEntityPathType,
  entityId?: number | string
): Promise<void> {
  if (entityType != null && entityId != null) {
    return queryClient.invalidateQueries({
      queryKey: checklistKeys.entity(boardId, entityType, entityId)
    });
  }
  return queryClient.invalidateQueries({ queryKey: checklistKeys.board(boardId) });
}

export function invalidateChecklistDetail(boardId: number, checklistId: number | string): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: checklistKeys.detail(boardId, checklistId)
  });
}

/** Invalidate all day windows for a checklist’s status-matrix. */
export function invalidateChecklistStatusMatrix(boardId: number, checklistId: number | string): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: [...checklistKeys.board(boardId), "status-matrix", checklistId]
  });
}

/** Invalidate status-log history for one checklist item (all assignee filters). */
export function invalidateChecklistItemStatusLog(boardId: number, itemId: number | string): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: [...checklistKeys.board(boardId), "item-status-log", itemId]
  });
}

/** Drop detail cache without refetch (e.g. after delete — invalidate would 404). */
export function removeChecklistDetail(boardId: number, checklistId: number | string): void {
  queryClient.removeQueries({
    queryKey: checklistKeys.detail(boardId, checklistId)
  });
  queryClient.removeQueries({
    queryKey: [...checklistKeys.board(boardId), "status-matrix", checklistId]
  });
}

type DetailItemsUpdater = (items: ChecklistItem[]) => ChecklistItem[];

/** The detail query is the editor's single source of items; every item write patches it. */
export function setChecklistDetailItems(
  boardId: number,
  checklistId: number | string,
  updater: DetailItemsUpdater
): void {
  queryClient.setQueryData<ChecklistDetail>(checklistKeys.detail(boardId, checklistId), (previous) =>
    previous ? { ...previous, items: updater(previous.items) } : previous
  );
}

/** Put a just-created checklist in the detail cache so the editor opens on it without a loading flash. */
export function seedChecklistDetail(boardId: number, detail: ChecklistDetail): void {
  queryClient.setQueryData(checklistKeys.detail(boardId, detail.checklist.id), detail);
}

type DetailSnapshot = { boardId: number; checklistId: number | string; previous: ChecklistDetail | undefined };

/** Optimistic item write: pause refetches, remember the cache, apply the change. */
async function applyOptimisticDetailItems(
  scope: ChecklistItemCacheScope,
  updater: DetailItemsUpdater
): Promise<DetailSnapshot | undefined> {
  if (scope.checklistId == null || scope.checklistId === "") return undefined;
  const { boardId, checklistId } = scope;
  const key = checklistKeys.detail(boardId, checklistId);
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<ChecklistDetail>(key);
  setChecklistDetailItems(boardId, checklistId, updater);
  return { boardId, checklistId, previous };
}

function rollbackDetail(snapshot: DetailSnapshot | undefined): void {
  if (snapshot?.previous) {
    queryClient.setQueryData(checklistKeys.detail(snapshot.boardId, snapshot.checklistId), snapshot.previous);
  }
}

function applyItemPatch(item: ChecklistItem, body: PatchChecklistItemPayload): ChecklistItem {
  const next: ChecklistItem = { ...item };
  if (body.description !== undefined) next.description = body.description;
  if (body.comment !== undefined) next.comment = body.comment;
  if (body.order !== undefined) next.order = body.order;
  if (body.status !== undefined) {
    const checked = body.status === CHECKLIST_ITEM_STATUS_TYPE.COMPLETED;
    next.status = body.status;
    next.checked = checked;
    if (!checked) {
      next.checkedBy = null;
      next.checkedDate = null;
    }
  }
  return next;
}

/**
 * Refresh entity list + board-wide Checklists lists, and the open checklist’s
 * status-matrix so the CheckGrid cells/subjects stay in sync. Detail is patched
 * optimistically by each mutation, not refetched here.
 */
function invalidateAfterItemWrite(scope: ChecklistItemCacheScope) {
  if (scope.entityType != null && scope.entityId != null) {
    void invalidateEntityChecklists(scope.boardId, scope.entityType, scope.entityId);
  }
  // Board Checklists rail / Right now (not only the entity-scoped EM list).
  void queryClient.invalidateQueries({
    queryKey: [...checklistKeys.board(scope.boardId), "board-list"]
  });
  if (scope.checklistId != null && scope.checklistId !== "") {
    void invalidateChecklistStatusMatrix(scope.boardId, scope.checklistId);
  }
}

export type CreateChecklistVariables = {
  boardId: number;
  body: CreateChecklistPayload;
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null;
};

/** POST /board/<board_id>/checklists/ — invalidates entity list on success. */
export function useCreateChecklistMutation() {
  return useMutation({
    mutationFn: ({ boardId, body }: CreateChecklistVariables) => createChecklist(boardId, body),
    onSuccess: (_data, vars) => {
      if (vars.entityType != null && vars.entityId != null) {
        void invalidateEntityChecklists(vars.boardId, vars.entityType, vars.entityId);
      } else {
        void invalidateEntityChecklists(vars.boardId);
      }
    }
  });
}

export type PatchChecklistVariables = {
  boardId: number;
  checklistId: number | string;
  body: PatchChecklistPayload;
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null;
};

/** Entity list (`list`) and board-wide list (`board-list`) share InfiniteData pages. */
function patchBoardChecklistListCaches(
  boardId: number,
  updater: (previous: InfiniteData<EntityChecklistsPage>) => InfiniteData<EntityChecklistsPage> | undefined
) {
  for (const segment of ["list", "board-list"] as const) {
    queryClient.setQueriesData<InfiniteData<EntityChecklistsPage>>(
      { queryKey: [...checklistKeys.board(boardId), segment] },
      (previous) => {
        if (!previous?.pages) return previous;
        return updater(previous);
      }
    );
  }
}

type ListSnapshot = [readonly unknown[], InfiniteData<EntityChecklistsPage> | undefined][];

function applyChecklistPatch(checklist: Checklist, body: PatchChecklistPayload): Checklist {
  const next: Checklist = { ...checklist };
  if (body.title !== undefined) next.title = body.title;
  if (body.archived !== undefined) next.archived = body.archived;
  if (body.can_edit_grant !== undefined) next.canEdit = body.can_edit_grant;
  if (body.can_view_grant !== undefined) next.canView = body.can_view_grant;
  if (body.recurrence !== undefined) next.recurrence = body.recurrence as Checklist["recurrence"];
  if (body.recurrence_weekdays !== undefined) next.recurrenceWeekdays = body.recurrence_weekdays;
  if (body.assigned_boardusers !== undefined) next.assignedBoardUsers = body.assigned_boardusers;
  return next;
}

/** PATCH /board/<board_id>/checklists/<id> — shows the change at once, rolls back on error, then merges the response into list + detail cache. */
export function usePatchChecklistMutation() {
  return useMutation({
    mutationFn: ({ boardId, checklistId, body }: PatchChecklistVariables) =>
      patchChecklist(boardId, checklistId, body),
    onMutate: async (vars) => {
      const checklistIdKey = String(vars.checklistId);
      const detailKey = checklistKeys.detail(vars.boardId, vars.checklistId);
      await queryClient.cancelQueries({ queryKey: detailKey });

      const lists: ListSnapshot = (["list", "board-list"] as const).flatMap((segment) =>
        queryClient.getQueriesData<InfiniteData<EntityChecklistsPage>>({
          queryKey: [...checklistKeys.board(vars.boardId), segment]
        })
      );
      const detail = queryClient.getQueryData<ChecklistDetail>(detailKey);

      patchBoardChecklistListCaches(vars.boardId, (previous) => ({
        ...previous,
        pages: previous.pages.map((page) => ({
          ...page,
          results: page.results.map((row) =>
            String(row.id) === checklistIdKey ? applyChecklistPatch(row, vars.body) : row
          )
        }))
      }));
      queryClient.setQueryData<ChecklistDetail>(detailKey, (previous) =>
        previous ? { ...previous, checklist: applyChecklistPatch(previous.checklist, vars.body) } : previous
      );
      return { lists, detail };
    },
    onError: (_error, vars, context) => {
      if (!context) return;
      for (const [key, data] of context.lists) queryClient.setQueryData(key, data);
      if (context.detail) {
        queryClient.setQueryData(checklistKeys.detail(vars.boardId, vars.checklistId), context.detail);
      }
    },
    onSuccess: (data, vars) => {
      if (data?.id == null) return;
      const checklistIdKey = String(vars.checklistId);

      patchBoardChecklistListCaches(vars.boardId, (previous) => ({
        ...previous,
        pages: previous.pages.map((page) => ({
          ...page,
          results: page.results.map((row) =>
            String(row.id) === checklistIdKey ? mergeChecklistRecordWrite(row, data) : row
          )
        }))
      }));

      queryClient.setQueryData<ChecklistDetail>(
        checklistKeys.detail(vars.boardId, vars.checklistId),
        (previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            checklist: mergeChecklistRecordWrite(previous.checklist, data)
          };
        }
      );

      // Assignees / recurrence drive matrix columns and day windows — refresh CheckGrid.
      void invalidateChecklistStatusMatrix(vars.boardId, vars.checklistId);
      // The PATCH response has no progress totals; they change with assignees and recurrence.
      if (
        vars.body.assigned_boardusers !== undefined ||
        vars.body.recurrence !== undefined ||
        vars.body.recurrence_weekdays !== undefined
      ) {
        void invalidateEntityChecklists(vars.boardId, vars.entityType ?? undefined, vars.entityId ?? undefined);
      }
      // Progress-filtered Right now lanes can change when assignees or archive flip.
      void queryClient.invalidateQueries({
        queryKey: [...checklistKeys.board(vars.boardId), "board-list"],
        predicate: (query) => Boolean((query.queryKey[3] as { progress?: string } | undefined)?.progress)
      });
    }
  });
}

export type DeleteChecklistVariables = {
  boardId: number;
  checklistId: number | string;
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null;
};

/**
 * DELETE /board/<board_id>/checklists/<id>
 *
 * Cancels in-flight detail fetches and drops the row from list cache first so
 * tile + editor (separate providers, same query key) disable their detail
 * queries before we remove the detail cache — otherwise removeQueries refetches
 * and 404s.
 */
export function useDeleteChecklistMutation() {
  return useMutation({
    mutationFn: ({ boardId, checklistId }: DeleteChecklistVariables) =>
      deleteChecklist(boardId, checklistId),
    onMutate: async (vars) => {
      const detailKey = checklistKeys.detail(vars.boardId, vars.checklistId);
      await queryClient.cancelQueries({ queryKey: detailKey });

      const checklistIdKey = String(vars.checklistId);
      patchBoardChecklistListCaches(vars.boardId, (previous) => {
        const hadItem = previous.pages.some((page) =>
          page.results.some((row) => String(row.id) === checklistIdKey)
        );
        return {
          ...previous,
          pages: previous.pages.map((page) => ({
            ...page,
            count: hadItem ? Math.max(0, page.count - 1) : page.count,
            results: page.results.filter((row) => String(row.id) !== checklistIdKey)
          }))
        };
      });
    },
    onSuccess: (_data, vars) => {
      removeChecklistDetail(vars.boardId, vars.checklistId);
      if (vars.entityType != null && vars.entityId != null) {
        void invalidateEntityChecklists(vars.boardId, vars.entityType, vars.entityId);
      } else {
        void invalidateEntityChecklists(vars.boardId);
      }
    }
  });
}

export type CreateChecklistItemVariables = ChecklistItemCacheScope & {
  body: CreateChecklistItemPayload;
  checklistId: number | string;
};

/** POST /board/<board_id>/checklists/<id>/items — shows the item at once with a draft id, swapped for the saved one. */
export function useCreateChecklistItemMutation() {
  return useMutation({
    mutationFn: ({ boardId, checklistId, body }: CreateChecklistItemVariables) =>
      createChecklistItem(boardId, checklistId, body),
    onMutate: async (vars) => {
      const draftId = nextDraftId("checklistitem");
      const snapshot = await applyOptimisticDetailItems(vars, (items) => {
        const checklist = queryClient.getQueryData<ChecklistDetail>(
          checklistKeys.detail(vars.boardId, vars.checklistId)
        )?.checklist;
        return [
          ...items,
          buildChecklistItem({
            id: draftId,
            description: vars.body.description ?? "",
            status: CHECKLIST_ITEM_STATUS_TYPE.UNSEEN,
            checklistId: vars.checklistId,
            order: vars.body.order ?? items.length,
            archived: false,
            checklist
          })
        ];
      });
      return { snapshot, draftId };
    },
    onError: (_error, _vars, context) => rollbackDetail(context?.snapshot),
    onSuccess: (data, vars, context) => {
      invalidateAfterItemWrite(vars);
      const saved = (checklist: Checklist | undefined) =>
        buildChecklistItem({
          id: data.id,
          description: data.description ?? "",
          status: data.status ?? CHECKLIST_ITEM_STATUS_TYPE.UNSEEN,
          checklistId: vars.checklistId,
          order: data.order ?? 0,
          archived: Boolean(data.archived),
          comment: data.comment ?? null,
          image: data.image_url ?? undefined,
          thumbnailUrl: data.thumbnail_url ?? undefined,
          checklist
        });
      queryClient.setQueryData<ChecklistDetail>(
        checklistKeys.detail(vars.boardId, vars.checklistId),
        (previous) => {
          if (!previous) return previous;
          const withoutDraft = previous.items.filter((item) => item.id !== context?.draftId);
          // A refetch that landed mid-flight may already contain the saved row.
          if (withoutDraft.some((item) => String(item.id) === String(data.id))) {
            return { ...previous, items: withoutDraft };
          }
          const draftIndex = previous.items.findIndex((item) => item.id === context?.draftId);
          const next = saved(previous.checklist);
          const items =
            draftIndex === -1
              ? [...withoutDraft, next]
              : [...withoutDraft.slice(0, draftIndex), next, ...withoutDraft.slice(draftIndex)];
          return { ...previous, items };
        }
      );
    }
  });
}

export type PatchChecklistItemVariables = ChecklistItemCacheScope & {
  itemId: number | string;
  body: PatchChecklistItemPayload;
};

/** PATCH /board/<board_id>/checklists/item/<itemId> */
export function usePatchChecklistItemMutation() {
  return useMutation({
    mutationFn: ({ boardId, itemId, body }: PatchChecklistItemVariables) =>
      patchChecklistItem(boardId, itemId, body),
    onMutate: (vars) =>
      applyOptimisticDetailItems(vars, (items) =>
        items.map((item) => (String(item.id) === String(vars.itemId) ? applyItemPatch(item, vars.body) : item))
      ),
    onError: (_error, _vars, snapshot) => rollbackDetail(snapshot),
    onSuccess: (_data, vars) => {
      invalidateAfterItemWrite(vars);
      // EM ticks share this endpoint with CheckGrid cell marks — bust status-log cache.
      void invalidateChecklistItemStatusLog(vars.boardId, vars.itemId);
    }
  });
}

export type DeleteChecklistItemVariables = ChecklistItemCacheScope & {
  itemId: number | string;
};

/** DELETE /board/<board_id>/checklists/item/<itemId> */
export function useDeleteChecklistItemMutation() {
  return useMutation({
    mutationFn: ({ boardId, itemId }: DeleteChecklistItemVariables) =>
      deleteChecklistItem(boardId, itemId),
    onMutate: (vars) =>
      applyOptimisticDetailItems(vars, (items) => items.filter((item) => String(item.id) !== String(vars.itemId))),
    onError: (_error, _vars, snapshot) => rollbackDetail(snapshot),
    onSuccess: (_data, vars) => {
      invalidateAfterItemWrite(vars);
      void invalidateChecklistItemStatusLog(vars.boardId, vars.itemId);
    }
  });
}

export type ImportChecklistItemsVariables = ChecklistItemCacheScope & {
  checklistId: number | string;
  checklistItemsData: string;
};

/** POST /checklist/<id>/items/import — refreshes list + detail (items replaced). */
export function useImportChecklistItemsMutation() {
  return useMutation({
    mutationFn: ({ checklistId, checklistItemsData }: ImportChecklistItemsVariables) =>
      importChecklistItems(checklistId, checklistItemsData),
    onSuccess: (_data, vars) => {
      invalidateAfterItemWrite(vars);
      void invalidateChecklistDetail(vars.boardId, vars.checklistId);
    }
  });
}

export type ExportChecklistItemsVariables = {
  checklistId: number | string;
  fileName?: string;
};

/** GET /checklist/<id>/items/export — downloads blob; no cache update. */
export function useExportChecklistItemsMutation() {
  return useMutation({
    mutationFn: ({ checklistId, fileName }: ExportChecklistItemsVariables) =>
      exportChecklistItems(checklistId, fileName)
  });
}

export type DuplicateChecklistVariables = {
  boardId: number;
  /** Source checklist whose items/structure are copied onto the newly created row. */
  sourceChecklistId: number | string;
  createBody: CreateChecklistPayload;
  duplicateBody: DuplicateChecklistPayload;
  entityType?: ChecklistEntityPathType | null;
  entityId?: number | string | null;
};

/**
 * Create a checklist then PUT /checklist/<newId>/duplicate with `duplicate_id`.
 * Rolls back the create if the duplicate step fails.
 */
export function useDuplicateChecklistMutation() {
  return useMutation({
    mutationFn: async ({ boardId, sourceChecklistId, createBody, duplicateBody }: DuplicateChecklistVariables) => {
      const created = await createChecklist(boardId, createBody);
      if (created?.id == null) throw new Error("missing id");
      try {
        await duplicateChecklist(created.id, {
          ...duplicateBody,
          duplicate_id: duplicateBody.duplicate_id ?? sourceChecklistId
        });
      } catch (error) {
        await deleteChecklist(boardId, created.id).catch(() => undefined);
        throw error;
      }
      return created;
    },
    onSuccess: (_data, vars) => {
      if (vars.entityType != null && vars.entityId != null) {
        void invalidateEntityChecklists(vars.boardId, vars.entityType, vars.entityId);
      } else {
        void invalidateEntityChecklists(vars.boardId);
      }
    }
  });
}

export type PatchChecklistCellVariables = {
  boardId: number;
  checklistId: number | string;
  checklistItemId: number | string;
  body: {
    checked?: boolean;
    comment?: string | null;
    applicability?: string;
    board_user_id?: number;
  };
};

/**
 * PATCH item status for a Checklists-view matrix cell.
 * Refreshes status-matrix + detail only (no board-list refetch).
 */
export function usePatchChecklistCellMutation() {
  return useMutation({
    mutationFn: ({ boardId, checklistItemId, body }: PatchChecklistCellVariables) => {
      const status =
        body.applicability === "not_applicable"
          ? null
          : typeof body.checked === "boolean"
            ? body.checked
              ? CHECKLIST_ITEM_STATUS_TYPE.COMPLETED
              : CHECKLIST_ITEM_STATUS_TYPE.NOT_COMPLETED
            : null;
      if (status == null) {
        throw new Error("Unsupported cell patch for checklist status-log API");
      }
      const payload: PatchChecklistItemPayload = { status };
      if (body.board_user_id != null) payload.board_user_id = body.board_user_id;
      if (body.comment !== undefined) payload.comment = body.comment;
      return patchChecklistItem(boardId, checklistItemId, payload);
    },
    onSuccess: (_data, vars) => {
      void invalidateChecklistStatusMatrix(vars.boardId, vars.checklistId);
      void invalidateChecklistDetail(vars.boardId, vars.checklistId);
      void invalidateChecklistItemStatusLog(vars.boardId, vars.checklistItemId);
      // A tick can move a checklist between not-started / in-progress / completed on the server.
      void queryClient.invalidateQueries({
        queryKey: [...checklistKeys.board(vars.boardId), "board-list"],
        predicate: (query) => Boolean((query.queryKey[3] as { progress?: string } | undefined)?.progress)
      });
    }
  });
}

/**
 * Imperative status-log load for the Checklists review tray (callback-driven UI).
 * Uses fetchQuery so repeat opens hit cache and concurrent loads dedupe.
 */
export function fetchChecklistItemStatusLogQuery(
  params: FetchChecklistItemStatusLogParams
): Promise<ChecklistItemStatusLogRow[]> {
  return queryClient.fetchQuery({
    queryKey: checklistKeys.itemStatusLog(params.boardId, params.itemId, params.boardUserId),
    queryFn: () => fetchChecklistItemStatusLog(params),
    staleTime: 30_000
  });
}

function patchDetailItemImage(
  boardId: number,
  checklistId: number | string,
  itemId: number | string,
  image: { image?: string; thumbnailUrl?: string | undefined } | null
) {
  setChecklistDetailItems(boardId, checklistId, (items) =>
    items.map((item) => {
      if (String(item.id) !== String(itemId)) return item;
      if (image == null) return { ...item, image: undefined, thumbnailUrl: undefined };
      return { ...item, image: image.image ?? item.image, thumbnailUrl: image.thumbnailUrl };
    })
  );
}

export type UploadChecklistItemImageVariables = ChecklistItemCacheScope & {
  itemId: number | string;
  file: File;
};

/** POST /board/<board_id>/checklists/item/<itemId>/upload */
export function useUploadChecklistItemImageMutation() {
  return useMutation({
    mutationFn: ({
      boardId,
      itemId,
      file
    }: UploadChecklistItemImageVariables): Promise<ChecklistItemImageUploadResponse> =>
      uploadChecklistItemImageRequest(boardId, itemId, file),
    onSuccess: (data, vars) => {
      if (vars.checklistId != null) {
        patchDetailItemImage(vars.boardId, vars.checklistId, vars.itemId, {
          image: data.image_url,
          thumbnailUrl: data.thumbnail_url ? `${data.thumbnail_url}?timestamp=${Date.now()}` : undefined
        });
      }
      invalidateAfterItemWrite(vars);
    }
  });
}

export type RemoveChecklistItemImageVariables = ChecklistItemCacheScope & {
  itemId: number | string;
};

/** PATCH /checklist/item/<itemId> with `remove_file: true` */
export function useRemoveChecklistItemImageMutation() {
  return useMutation({
    mutationFn: ({ itemId }: RemoveChecklistItemImageVariables) => removeChecklistItemImageRequest(itemId),
    onSuccess: (_data, vars) => {
      if (vars.checklistId != null) {
        patchDetailItemImage(vars.boardId, vars.checklistId, vars.itemId, null);
      }
      invalidateAfterItemWrite(vars);
    }
  });
}

export type SendChecklistNudgeVariables = {
  checklistId: number | string;
};

/** POST /checklist/<id>/nudge — notification only; no list/detail cache change. */
export function useSendChecklistNudgeMutation() {
  return useMutation({
    mutationFn: ({ checklistId }: SendChecklistNudgeVariables) => sendChecklistNudge(checklistId)
  });
}
